// @ts-nocheck
import { useQueryClient, useMutation } from 'react-query';
import { useRequestQuery } from '../useQueryRequest';
import { transformPagination } from '@/utils';
import useApiRequest from '../useRequest';
import t from './types';
import { useRequestPdf } from '../useRequestPdf';

const commonInvalidateQueries = (queryClient) => {
  // Invalidate purchase orders.
  queryClient.invalidateQueries(t.PURCHASE_ORDERS);

  // Invalidate items.
  queryClient.invalidateQueries(t.ITEMS);
  queryClient.invalidateQueries(t.ITEM);

  // Invalidate vendors.
  queryClient.invalidateQueries([t.VENDORS]);
  queryClient.invalidateQueries(t.VENDOR);

  // Invalidate accounts.
  queryClient.invalidateQueries(t.ACCOUNTS);
  queryClient.invalidateQueries(t.ACCOUNT);

  // Invalidate landed cost.
  queryClient.invalidateQueries(t.LANDED_COST);
  queryClient.invalidateQueries(t.LANDED_COST_TRANSACTION);

  // Invalidate reconcile.
  queryClient.invalidateQueries(t.RECONCILE_VENDOR_CREDIT);
  queryClient.invalidateQueries(t.RECONCILE_VENDOR_CREDITS);

  // Invalidate financial reports.
  queryClient.invalidateQueries(t.FINANCIAL_REPORT);

  // Invalidate the transactions by reference.
  queryClient.invalidateQueries(t.TRANSACTIONS_BY_REFERENCE);

  // Invalidate items associated purchase order transactions.
  queryClient.invalidateQueries(t.ITEMS_ASSOCIATED_WITH_PURCHASE_ORDERS);

  // Invalidate item warehouses.
  queryClient.invalidateQueries(t.ITEM_WAREHOUSES_LOCATION);

  // Invalidate mutate base currency abilities.
  queryClient.invalidateQueries(t.ORGANIZATION_MUTATE_BASE_CURRENCY_ABILITIES);
};

/**
 * Creates a new purchase order.
 */
export function useCreatePurchaseOrder(props) {
  const queryClient = useQueryClient();
  const apiRequest = useApiRequest();

  return useMutation((values) => apiRequest.post('purchases/purchase-orders', values), {
    onSuccess: (res, values) => {
      // Common invalidate queries.
      commonInvalidateQueries(queryClient);
    },
    ...props,
  });
}

/**
 * Edits the given purchase order.
 */
export function useEditPurchaseOrder(props) {
  const queryClient = useQueryClient();
  const apiRequest = useApiRequest();

  return useMutation(
    ([id, values]) => apiRequest.post(`purchases/purchase-orders/${id}`, values),
    {
      onSuccess: (res, [id, values]) => {
        // Common invalidate queries.
        commonInvalidateQueries(queryClient);

        // Invalidate purchase order query.
        queryClient.invalidateQueries([t.PURCHASE_ORDER, id]);
      },
      ...props,
    },
  );
}

/**
 * Marks the given purchase order as open.
 */
// export function useOpenPurchaseOrder(props) {
//   const queryClient = useQueryClient();
//   const apiRequest = useApiRequest();

//   return useMutation((id) => apiRequest.post(`purchases/purchase-orders/${id}/open`), {
//     onSuccess: (res, id) => {
//       // Common invalidate queries.
//       commonInvalidateQueries(queryClient);

//       // Invalidate purchase order query.
//       queryClient.invalidateQueries([t.PURCHASE_ORDER, id]);
//     },
//     ...props,
//   });
// }

/**
 * Deletes the given purchase order.
 */
export function useDeletePurchaseOrder(props) {
  const queryClient = useQueryClient();
  const apiRequest = useApiRequest();

  return useMutation((id) => apiRequest.delete(`purchases/purchase-orders/${id}`), {
    onSuccess: (res, id) => {
      // Common invalidate queries.
      commonInvalidateQueries(queryClient);

      // Invalidate purchase order query.
      queryClient.invalidateQueries([t.PURCHASE_ORDER, id]);
    },
    ...props,
  });
}

const transformPurchaseOrdersResponse = (response) => ({
  purchase_orders: response.data.purchase_orders,
  pagination: transformPagination(response.data.pagination),
  filterMeta: response.data.filter_meta,
});

/**
 * Retrieve purchase orders list with pagination meta.
 */
export function usePurchaseOrders(query, props) {
  return useRequestQuery(
    [t.PURCHASE_ORDERS, query],
    {
      method: 'get',
      url: 'purchases/purchase-orders',
      params: query,
    },
    {
      select: transformPurchaseOrdersResponse,
      defaultData: {
        purchase_orders: [],
        pagination: {
          page: 1,
          page_size: 12,
          total: 0,
        },
        filterMeta: {},
      },
      ...props,
    },
  );
}

/**
 * Retrieve purchase order details of the given purchase order id.
 * @param {number} id - Purchase order id.
 */
export function usePurchaseOrder(id, props) {
  return useRequestQuery(
    [t.PURCHASE_ORDER, id],
    { method: 'get', url: `/purchases/purchase-orders/${id}` },
    {
      select: (res) => res.data.purchase_order,
      defaultData: {},
      ...props,
    },
  );
}

/**
 * Retrieve the estimate pdf document data,
 */

export function usePdfPurchaseOrder(id) {
 return useRequestPdf({
   url: id ? `purchases/purchase-orders/${id}` : null,
   enabled: !!id,
 });
}

export function usePdfPoOrder(id) {
  return useRequestPdf({
    url: id ? `sales/po/order/${id}/pdf` : null,
    enabled: !!id,
  });
}

export function usePoOrder(id, props?) {
  return useRequestQuery(
    ['PO_ORDER', id],
    { method: 'get', url: `sales/po/order/${id}` },
    {
      select: (res) => res.data.data,
      defaultData: null,
      enabled: !!id,
      ...props,
    },
  );
}

export function usePoOrders(props?) {
  return useRequestQuery(
    ['PO_ORDERS'],
    { method: 'get', url: 'sales/po' },
    {
      select: (res) => res.data.data,
      defaultData: [],
      ...props,
    },
  );
}
/**
 * Retrieve the due purchase orders of the given vendor id.
 * @param {number} vendorId -
 */
// export function useDuePurchaseOrders(vendorId, props) {
//   return useRequestQuery(
//     [t.PURCHASE_ORDERS, t.PURCHASE_ORDERS_DUE, vendorId],
//     {
//       method: 'get',
//       url: 'purchases/purchase-orders/due',
//       params: { vendor_id: vendorId },
//     },
//     {
//       select: (res) => res.data.purchase_orders,
//       defaultData: [],
//       ...props,
//     },
//   );
// }

export function useRefreshPurchaseOrders() {
  const queryClient = useQueryClient();

  return {
    refresh: () => {
      queryClient.invalidateQueries(t.PURCHASE_ORDERS);
    },
  };
}

// export function usePurchaseOrderPaymentTransactions(id, props) {
//   return useRequestQuery(
//     [t.PURCHASE_ORDERS_PAYMENT_TRANSACTIONS, id],
//     {
//       method: 'get',
//       url: `purchases/purchase-orders/${id}/payment-transactions`,
//     },
//     {
//       select: (res) => res.data.data,
//       defaultData: [],
//       ...props,
//     },
//   );
// }
